/**
 * User: n.gorte
 * Date: 14.09.2020
 * Time: 16:03
 */

import React, { useEffect, useState } from 'external/react';
import PropTypes from 'external/prop-types';
import { Button, Form } from 'external/react-bootstrap';
import { DateRangePicker, Icon, PDFExportButton } from 'plugin/base-portlet';
import JournalActions from '../actions/JournalActions';
import { AppContext, ConfigActions } from 'plugin/liferay-widgets-react-web';
import { MAX_JOURNAL_FILTER_DATE_RANGE_DAYS } from '../constants/Constants';
import datefns from 'external/datefns';
import { FILTER_DEFINITIONS } from '../constants/FilterDefinitions';

export const JournalFilters = ({ localConfigKey, listName }) => {
    const [showFilters, setShowFilters] = useState(false);
    const localConfig = JSON.parse(localStorage.getItem(localConfigKey));
    let defaultCategoryKeys = localConfig && localConfig.properties && localConfig.properties.categoryKeys || '';
    let dateFilterActive = localConfig && localConfig.properties && localConfig.properties.dateFilterActive;
    let start = localConfig && localConfig.properties && localConfig.properties.startDate;
    let end = localConfig && localConfig.properties && localConfig.properties.endDate;
    const [{ startDate, endDate }, setStartEndDate] = useState({
        startDate: start && new Date(start),
        endDate: start && new Date(end)
    });
    const [categoryKeys, setCategoryKeys] = useState(defaultCategoryKeys.toString());
    const [useDateFilter, setUseDateFilter] = useState(dateFilterActive);

    useEffect(() => {
        const reportWindowSize = () => {
            const width = window.innerWidth;
            if (width >= window.BREAKPOINT_LG) {
                setShowFilters(true);
            }
        };
        window.addEventListener('resize', reportWindowSize);
        reportWindowSize();

        return () => window.removeEventListener('resize', reportWindowSize);
    }, []);

    useEffect(() => {
        if (!useDateFilter) {
            setStartEndDate({ startDate: null, endDate: null });
        }
    }, [useDateFilter]);

    useEffect(() => {
        ConfigActions.updateLocalConfig(localConfigKey, {
            categoryKeys,
            dateFilterActive: useDateFilter,
            startDate: startDate && startDate.getTime(),
            endDate: endDate && endDate.getTime()
        });
        JournalActions.search(listName, {
            categoryKeys,
            startDate,
            endDate,
            start: 0,
            end: AppContext.DEFAULT_LIST_SIZE
        });
    }, [startDate, endDate, categoryKeys, useDateFilter]);

    const toggleFilter = (value, selected) => () => {
        let categoryKeysArray = categoryKeys.split(',');
        if (Array.isArray(value)) {
            categoryKeysArray = selected ? categoryKeysArray.filter(key => value === key) : Array.from(new Set([...categoryKeysArray, ...value]));
        } else {
            categoryKeysArray = selected ? categoryKeysArray.filter(key => key !== value) : [...categoryKeysArray, value];
        }
        setCategoryKeys(categoryKeysArray.toString());
    };

    const allowExport = (e) => {
        const differenceInCalendarDays = (startDate && endDate) ? datefns.differenceInCalendarDays(new Date(startDate), new Date(endDate)) : Infinity;
        const allowed = Math.abs(differenceInCalendarDays) <= MAX_JOURNAL_FILTER_DATE_RANGE_DAYS;
        if (!allowed) {
            window.alert('Für den Export muss ein Zeitraum von maximal 14 Tagen ausgewählt werden.');
        }
        return allowed;
    };

    const startEndDateChanged = ({ startDate, endDate }) => {
        setStartEndDate({ startDate: datefns.startOfDay(startDate), endDate: datefns.endOfDay(endDate) });
    };

    return (
        <div className="journal-portlet-filters py-3 border-right border-bottom" role="toolbar">
            <div className="d-flex mb-3 justify-content-between">
                <Button
                    className="d-lg-none"
                    onClick={() => setShowFilters(!showFilters)}
                >
                    <div className="d-flex align-items-center">
                        Filter
                        <Icon name={showFilters ? 'arrow-down' : 'arrow-up'} className="ml-2"/>
                    </div>
                </Button>
                <PDFExportButton
                    baseUrl={window.journalExportUrl}
                    namespace={window.journalExportPortletNamespace}
                    title="Bericht erzeugen"
                    fileName="Journal"
                    allowExport={allowExport}
                    params={{
                        startDate: startDate && startDate.getTime(),
                        endDate: endDate && endDate.getTime(),
                        categoryKeys,
                    }}
                />
            </div>

            {
                showFilters && (
                    <React.Fragment>
                        <div className="my-3 py-3 border-top border-bottom">
                            <div className="h6 d-flex align-items-center">
                                Datumsfilter
                            </div>
                            <Form.Group controlId="date-filter-checkbox">
                                <Form.Check
                                    className="my-3"
                                    type="checkbox"
                                    checked={useDateFilter}
                                    onChange={e => setUseDateFilter(e.target.checked)}
                                    label="Datumsfilter aktivieren"
                                >
                                </Form.Check>
                            </Form.Group>
                            <DateRangePicker
                                dateFormat="dd.MM.yyyy"
                                startDate={startDate}
                                endDate={endDate}
                                onChange={startEndDateChanged}
                                disabled={!useDateFilter}
                            />
                        </div>
                        {
                            FILTER_DEFINITIONS.map(filter => {
                                return (
                                    <div key={filter.label} className="mb-3">
                                        <span className="h6 d-block mb-3">{filter.label}</span>

                                        {
                                            filter.options.map(o => {
                                                let checked;
                                                if (Array.isArray(o.value)) {
                                                    checked = true;
                                                    o.value.forEach(entry => {
                                                        let singleChecked = false;
                                                        categoryKeys.split(',').forEach(key => {
                                                            if (key === entry) {
                                                                singleChecked = true;
                                                                return;
                                                            }
                                                        });
                                                        if (!singleChecked) {
                                                            checked = false;
                                                        }
                                                    });
                                                } else {
                                                    checked = false;
                                                    categoryKeys.split(',').forEach(key => {
                                                        if (key === o.value) {
                                                            checked = true;
                                                            return;
                                                        }
                                                    });
                                                }
                                                return (
                                                    <Form.Group
                                                        key={`filter-checkbox-${o.value}`}
                                                        controlId={`filter-checkbox-${o.value}`}
                                                    >
                                                        <Form.Check
                                                            type="checkbox"
                                                            checked={checked}
                                                            onChange={toggleFilter(o.value, checked)}
                                                            label={o.label}
                                                        />
                                                    </Form.Group>
                                                );
                                            })
                                        }
                                    </div>
                                );
                            })
                        }
                    </React.Fragment>
                )
            }

        </div>
    );
};

JournalFilters.propTypes = {
    localConfigKey: PropTypes.string,
    listName: PropTypes.string,
    defaultCategoryKeys: PropTypes.string,
    dateFilterActive: PropTypes.bool,
};



// WEBPACK FOOTER //
// ./js/components/JournalFilters.jsx